import { useState } from 'react'
import { useTableStore } from '@/stores/tableStore'
import type { RestaurantTable } from '@/types'

interface TableFormProps {
  table: RestaurantTable | null
  restaurantId: string
  onClose: () => void
}

export default function TableForm({ table, restaurantId, onClose }: TableFormProps) {
  const { createTable, updateTable, isLoading } = useTableStore()

  const [formData, setFormData] = useState({
    tableNumber: table?.tableNumber || '',
    location: table?.location || '',
    capacity: table?.capacity || 4,
    active: table?.active ?? true,
    notes: table?.notes || '',
  })

  const isEditing = !!table

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseInt(value) : value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (isEditing && table) {
        await updateTable(table.id, formData)
      } else {
        await createTable(restaurantId, formData)
      }
      onClose()
    } catch (error) {
      // Error handled by store
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800/95 dark:backdrop-blur-xl rounded-lg shadow-xl dark:shadow-2xl dark:shadow-slate-950/50 border dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 max-w-md w-full p-6 transition-all duration-300">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white dark:drop-shadow-[0_2px_10px_rgba(255,255,255,0.15)]">
            {isEditing ? 'Modifica Tavolo' : 'Nuovo Tavolo'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 text-2xl leading-none transition-colors"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Table Number */}
          <div>
            <label htmlFor="tableNumber" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Numero Tavolo *
            </label>
            <input
              id="tableNumber"
              name="tableNumber"
              type="text"
              required
              value={formData.tableNumber}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="es. 1, A1, T-01"
            />
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
              Identificativo univoco del tavolo
            </p>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Ubicazione
            </label>
            <input
              id="location"
              name="location"
              type="text"
              value={formData.location}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="es. Sala principale, Terrazza, Piano 1"
            />
          </div>

          {/* Capacity */}
          <div>
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Capacità (Persone)
            </label>
            <input
              id="capacity"
              name="capacity"
              type="number"
              min={1}
              max={20}
              value={formData.capacity}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
              Numero massimo di persone che possono sedersi
            </p>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Note
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={2}
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="Note aggiuntive sul tavolo"
            />
          </div>

          {/* Active Checkbox */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-blue-600 dark:text-blue-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                Tavolo attivo
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 ml-6">
              I tavoli disattivati non saranno visibili ai clienti
            </p>
          </div>

          {/* Info Box */}
          {!isEditing && (
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700/50 dark:ring-1 dark:ring-blue-500/30 rounded-md p-3">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                ℹ️ Un codice QR verrà generato automaticamente alla creazione del tavolo
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-slate-700 mt-6 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 shadow-lg dark:shadow-blue-900/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Salvataggio...' : isEditing ? 'Aggiorna' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
